import { useCallback, useEffect, useState } from 'react'
import { cx, DataTable, ErrorBox, Field, InfoBox, Panel, ScreenHeading, Tag, WarnBox } from '../../components/ui'
import { api, ApiError, errorMessage } from '../../lib/api'
import { useAuth, type ModelInfo } from '../../lib/auth'
import { dateTime, monthLabel } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { IndexLevelsEditor, MarketEditor } from './Monthly'
import type { VersionSummary } from '../../lib/types'

type Role = 'required' | 'optional' | 'fallback' | 'monthly'
const DATASETS: { id: string; label: string; columns: string; note: string; role: Role }[] = [
  { id: 'sales_transactions', role: 'required', label: 'sales_transactions.csv', columns: 'order_date, order_id, customer_id, product_id, units, net_price | revenue_net, unit_cost [, list_price, discount_pct, freight, incoterms, order_type, contract_id, sales_rep, sales_team, territory, country, zip, customer_name, product_name, product_main_category, product_sub_category, account_tier, vertical, channel, uom, is_commodity]', note: 'One file of order lines. Derives customers, articles (most frequent category, cost over the last twelve months) and the monthly history; keeps the lines for discount and benchmark analyses. Existing standing terms and index shares are kept.' },
  { id: 'product_scores', role: 'required', label: 'product_scores.csv', columns: 'product_id | sub_category | category, driver, level', note: 'Exactly one entity column per row. Unknown driver ids reject the file.' },
  { id: 'customer_scores', role: 'required', label: 'customer_scores.csv', columns: 'customer_id | sales_team | territory, driver, level', note: 'Exactly one entity column per row.' },
  { id: 'market_scores', role: 'monthly', label: 'Market situation', columns: '', note: '' },
  { id: 'index_levels', role: 'monthly', label: 'Index levels', columns: '', note: '' },
  { id: 'cost_structure', role: 'optional', label: 'cost_structure.csv', columns: 'product_id [, raw_index, raw_share, energy_index, energy_share, fixed_cost, cost_factor]', note: 'Which index each article’s cost follows, and by what share; the rest of the cost stays fixed. fixed_cost replaces the calculated cost and does not follow the indices; cost_factor multiplies it (1.05 = 5 % above). The file is complete: an article it leaves out is costed at its plain average again.' },
  { id: 'customers', role: 'optional', label: 'customers.csv', columns: 'customer_id, name, sales_team, territory, country [, zip, deal_<driver> …]', note: 'Standing terms are optional and default the deal attributes.' },
  { id: 'routes', role: 'optional', label: 'routes.csv', columns: 'plant, zip, cost_per_unit [, country, min_qty]', note: 'Cost per unit from a plant to a postcode or postcode prefix; the longest match wins. Rows with a higher min_qty are volume tiers.' },
  { id: 'differentials', role: 'optional', label: 'differentials.csv', columns: 'product_id, variant, amount_per_unit', note: '' },
  { id: 'sales_history', role: 'fallback', label: 'sales_history.csv', columns: 'month, customer_id, product_id, units, revenue_net, unit_cost [, freight]', note: 'Rows with units ≤ 0 or revenue_net ≤ 0 are dropped and counted.' },
  { id: 'products', role: 'fallback', label: 'products.csv', columns: 'product_id, name, category, sub_category, avg_unit_cost, raw_index, raw_share, energy_index, energy_share, rest_share, fixed_cost?, cost_factor?', note: 'Shares must sum to 1 (± 0.001); every index must exist. fixed_cost replaces the calculated cost and does not follow the indices; cost_factor multiplies it (1.05 = 5 % above).' },
]
const ROLE_LABEL: Record<Role, string> = { required: 'required', optional: 'optional', fallback: 'only without transactions', monthly: 'entered monthly' }

interface DatasetStatus {
  dataset: string
  rows: number
  from: string | null
  to: string | null
  detail: string | null
  detailCount: number | null
  lastImport: { at: string; by: string | null } | null
  freshness: 'ok' | 'stale' | 'missing' | 'none'
}

/** A template per dataset: the header the importer expects and one example row, drivers taken from the active model. */
function template(dataset: string, model: ModelInfo | null): string {
  const drv = (grain: string) => model?.drivers.find((d) => d.grain === grain)
  const deal = model?.drivers.filter((d) => d.grain === 'deal') ?? []
  const p = drv('product')
  const c = drv('customer')
  const m = drv('market')
  const month = new Date().toISOString().slice(0, 7)
  const rows: Record<string, string[][]> = {
    sales_transactions: [
      ['order_date', 'order_id', 'customer_id', 'customer_name', 'sales_team', 'territory', 'country', 'sales_rep', 'product_id', 'product_name', 'product_main_category', 'product_sub_category', 'units', 'list_price', 'net_price', 'revenue_net', 'unit_cost', 'freight', 'incoterms', 'order_type', 'contract_id'],
      [`${month}-03`, 'SO-100001', 'C-001', 'Example Customer GmbH', 'Team North', 'North', 'AT', 'A. Rep', 'SKU-001', 'Example article', 'Category', 'Sub-category', '10', '120.00', '110.00', '1100.00', '80.00', '12.50', 'DAP', 'stock', ''],
    ],
    index_levels: [['index', 'month', 'value'], ['RESIN', month, '104.2']],
    cost_structure: [
      ['product_id', 'raw_index', 'raw_share', 'energy_index', 'energy_share', 'fixed_cost', 'cost_factor'],
      ['SKU-001', 'RESIN', '0.55', 'GAS', '0.18', '', ''],
      ['SKU-002', '', '', '', '', '92.40', ''],
      ['SKU-003', 'RESIN', '0.5', 'GAS', '0.2', '', '1.05'],
    ],
    products: [
      ['product_id', 'name', 'category', 'sub_category', 'avg_unit_cost', 'raw_index', 'raw_share', 'energy_index', 'energy_share', 'rest_share', 'fixed_cost', 'cost_factor'],
      ['SKU-001', 'Example article', 'Category', 'Sub-category', '80.00', 'RESIN', '0.55', 'GAS', '0.18', '0.27', '', ''],
      ['SKU-002', 'Traded article', 'Category', 'Sub-category', '95.00', '', '0', '', '0', '1', '92.40', ''],
      ['SKU-003', 'Article costed low', 'Category', 'Sub-category', '60.00', 'RESIN', '0.5', 'GAS', '0.2', '0.3', '', '1.05'],
    ],
    customers: [
      ['customer_id', 'name', 'sales_team', 'territory', 'country', 'zip', ...deal.map((d) => `deal_${d.id}`)],
      ['C-001', 'Example Customer GmbH', 'Team North', 'North', 'AT', '4020', ...deal.map((d) => d.levels[0] ?? '')],
    ],
    routes: [
      ['plant', 'country', 'zip', 'min_qty', 'cost_per_unit'],
      ['Plant North', 'AT', '40', '0', '12.50'],
      ['Plant North', 'AT', '40', '20', '10.80'],
      ['Plant North', 'DE', '8', '0', '21.00'],
    ],
    sales_history: [['month', 'customer_id', 'product_id', 'units', 'revenue_net', 'unit_cost', 'freight'], [month, 'C-001', 'SKU-001', '10', '1100.00', '80.00', '12.50']],
    differentials: [['product_id', 'variant', 'amount_per_unit'], ['SKU-001', '25 kg bag', '8.00']],
    product_scores: [
      ['product_id', 'sub_category', 'category', 'driver', 'level'],
      ['SKU-001', '', '', p?.id ?? 'driver_id', p?.levels[2] ?? '3'],
      ['', 'Sub-category', '', p?.id ?? 'driver_id', p?.levels[1] ?? '2'],
    ],
    customer_scores: [
      ['customer_id', 'sales_team', 'territory', 'driver', 'level'],
      ['C-001', '', '', c?.id ?? 'driver_id', c?.levels[3] ?? '4'],
      ['', 'Team North', '', c?.id ?? 'driver_id', c?.levels[1] ?? '2'],
    ],
    market_scores: [
      ['month', 'territory', 'driver', 'level'],
      [month, 'North', m?.id ?? 'driver_id', m?.levels[1] ?? '2'],
      [month, '*', m?.id ?? 'driver_id', m?.levels[1] ?? '2'],
    ],
  }
  const q = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
  return (rows[dataset] ?? []).map((r) => r.map(q).join(',')).join('\n') + '\n'
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const FRESH_TONE = { ok: 'pos', stale: 'warn', missing: 'neg', none: 'neutral' } as const
const FRESH_LABEL = { ok: 'Current', stale: 'Behind', missing: 'Missing', none: '' } as const

interface ImpactPair {
  customerId: string
  customerName: string
  productId: string
  productName: string
  revenue: number
  before: number
  after: number
  delta: number
  deltaPct: number
  clamped: 'capped' | 'floored' | null
}
interface ImportResult {
  dataset: string
  inserted: number
  dropped: { line: number; reason: string }[]
  modelVersionId?: string
  dryRun: boolean
  derived?: { customers: number; products: number; historyRows: number; months: { from: string; to: string } | null }
  preview?: {
    added: number
    changed: number
    removed: number
    unchanged: number
    byDriver: { driverId: string; added: number; changed: number; removed: number }[]
    window: { from: string; to: string }
    totalPairs: number
    unratedBefore: number
    unratedAfter: number
    impact: { pairs: number; revenue: number; revenueShare: number; deltaEurAnnual: number; deltaPct: number; top: ImpactPair[] }
  }
}

/** §7 — all-or-nothing CSV import with line-numbered rejections. */
export function Imports() {
  const { t } = useT()
  const { model } = useAuth()
  const [status, setStatus] = useState<{ month: string; datasets: DatasetStatus[] } | null>(null)
  const loadStatus = useCallback(() => api.get<{ month: string; datasets: DatasetStatus[] }>('/api/admin/data-status').then(setStatus).catch(() => {}), [])
  const [tab, setTab] = useState<'monthly' | 'files'>('monthly')
  const [dataset, setDataset] = useState('sales_transactions')
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [versions, setVersions] = useState<VersionSummary[]>([])
  const [versionId, setVersionId] = useState('')
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<ImportResult | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [errors, setErrors] = useState<{ line: number; reason: string }[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.get<VersionSummary[]>('/api/model-versions').then(setVersions).catch(() => {})
    loadStatus()
  }, [loadStatus])

  const def = DATASETS.find((d) => d.id === dataset && d.role !== 'monthly') ?? DATASETS[0]!
  const isScores = dataset.endsWith('_scores')

  const run = async (dryRun: boolean) => {
    setBusy(true)
    setError(null)
    setErrors(null)
    if (dryRun) setPreview(null)
    else setResult(null)
    try {
      const params = new URLSearchParams()
      if (isScores && versionId) params.set('modelVersionId', versionId)
      if (dryRun) params.set('dryRun', 'true')
      const qs = params.toString() ? `?${params}` : ''
      const r = await api.postText<ImportResult>(`/api/admin/import/${dataset}${qs}`, text)
      if (dryRun) setPreview(r)
      else {
        setResult(r)
        setPreview(null)
        loadStatus()
      }
    } catch (e) {
      if (e instanceof ApiError && e.details && typeof e.details === 'object' && 'errors' in e.details) {
        setErrors((e.details as { errors: { line: number; reason: string }[] }).errors)
        setError(e.message)
      } else setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <ScreenHeading title={t('Reference data')} />
      <div className="mb-4 flex gap-1">
        {(['monthly', 'files'] as const).map((k) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={cx('rounded-md px-2.5 py-1 text-sm transition', tab === k ? 'bg-slate-800/60 text-slate-100' : 'text-slate-400 hover:text-slate-200')}>
            {k === 'monthly' ? t('Monthly inputs') : t('Files')}
          </button>
        ))}
      </div>
      {tab === 'monthly' && (
        <div className="space-y-4">
          <MarketEditor />
          <IndexLevelsEditor />
        </div>
      )}
      {tab === 'files' && (
      <>
      <Panel title={t('Data status')} className="mb-4" right={status ? <span className="text-[12px] text-slate-500">{t('as of')} {monthLabel(status.month)}</span> : null}>
        <DataTable
          rows={DATASETS.map((d) => ({ def: d, st: status?.datasets.find((x) => x.dataset === d.id) }))}
          rowKey={(r) => r.def.id}
          onRowClick={(r) => {
            if (r.def.role === 'monthly') {
              setTab('monthly')
              return
            }
            setDataset(r.def.id)
            setResult(null)
            setPreview(null)
            setErrors(null)
            setError(null)
          }}
          columns={[
            {
              key: 'd',
              header: t('Dataset'),
              render: (r) => (
                <span className={cx('text-[13px]', r.def.role !== 'monthly' && 'font-mono', r.def.id === dataset ? 'font-semibold text-sky-500' : 'text-slate-200')}>{t(r.def.label)}</span>
              ),
            },
            { key: 'r', header: '', render: (r) => <span className="text-[12px] text-slate-500">{t(ROLE_LABEL[r.def.role])}</span> },
            { key: 'n', header: t('Rows'), align: 'right', render: (r) => (r.st ? r.st.rows.toLocaleString('de-AT') : '–') },
            { key: 'c', header: t('Covers'), render: (r) => (r.st?.from && r.st.to ? <span className="tabular">{monthLabel(r.st.from)} – {monthLabel(r.st.to)}</span> : <span className="text-slate-600">–</span>) },
            { key: 'x', header: '', render: (r) => (r.st?.detail ? <span className="text-[13px] text-slate-500">{r.st.detailCount} {t(r.st.detail)}</span> : null) },
            { key: 'l', header: t('Last update'), render: (r) => (r.st?.lastImport ? <span className="text-[13px] text-slate-400">{dateTime(r.st.lastImport.at)}{r.st.lastImport.by ? ` · ${r.st.lastImport.by}` : ''}</span> : <span className="text-slate-600">–</span>) },
            { key: 's', header: '', align: 'right', render: (r) => (r.st && r.st.freshness !== 'none' ? <Tag tone={FRESH_TONE[r.st.freshness]}>{t(FRESH_LABEL[r.st.freshness])}</Tag> : null) },
          ]}
        />
      </Panel>
      <div>
        <Panel
          title={def.label}
          subtitle={<span className="font-mono">{def.columns}</span>}
          right={
            <button type="button" className="btn btn-sm" onClick={() => download(def.label.replace('.csv', '_template.csv'), template(def.id, model))}>
              {t('Download template')}
            </button>
          }
        >
          {def.note && <p className="mb-3 text-xs text-slate-500">{t(def.note)}</p>}
          {isScores && (
            <Field label={t('Target model version')} hint={t('Ratings are keyed by version so a rollback restores the pair. Defaults to the active version.')}>
              <select className="field w-full max-w-lg" value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                <option value="">{t('Active version')}</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.id.slice(0, 8)} · {t(v.status)} · {v.note || t('(no note)')}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="btn cursor-pointer">
              {t('Choose file…')}
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]
                  if (!f) return
                  setFileName(f.name)
                  setText(await f.text())
                }}
              />
            </label>
            {fileName && <span className="text-xs text-slate-400">{fileName}</span>}
            <span className="text-xs text-slate-500">{t('or paste below')}</span>
          </div>
          <textarea
            className="field mt-3 font-mono text-xs"
            rows={12}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setPreview(null)
            }}
            placeholder={def.columns.replace(/ \[.*\]/, '').replace(/ \| /g, ',') + '\n…'}
          />
          <div className="mt-3 flex items-center justify-end gap-3">
            <span className="mr-auto text-xs text-slate-500">{isScores ? t('Ratings move live prices: preview first, then import.') : t('Preview validates the file without writing.')}</span>
            <button type="button" className="btn" disabled={!text.trim() || busy} onClick={() => run(true)}>
              {busy && !preview ? t('Checking…') : t('Preview')}
            </button>
            <button type="button" className="btn btn-primary" disabled={!text.trim() || busy || !preview} onClick={() => run(false)} title={!preview ? t('Preview first') : ''}>
              {busy && preview ? t('Importing…') : t('Import')}
            </button>
          </div>
          {preview && (
            <div className="mt-3 space-y-3">
              <InfoBox>
                {t('Valid.')} <b>{preview.inserted}</b> {t('rows would be written')}{preview.dropped.length ? `, ${t('{n} dropped and counted', { n: preview.dropped.length })}` : ''}. {t('Nothing has been changed yet.')}
              </InfoBox>
              {preview.preview && (
                <div className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
                  <div className="mb-2 text-sm text-slate-200">
                    {t('Ratings')}: <b className="tabular text-emerald-400">{preview.preview.added}</b> {t('added')} · <b className="tabular text-amber-400">{preview.preview.changed}</b> {t('changed')} ·{' '}
                    <b className="tabular text-rose-400">{preview.preview.removed}</b> {t('removed')} · <span className="text-slate-500">{preview.preview.unchanged} {t('unchanged')}</span>
                  </div>
                  <div className="mb-3 text-xs text-slate-500">
                    {preview.preview.byDriver.map((d) => `${d.driverId}: +${d.added} ~${d.changed} −${d.removed}`).join(' · ')}
                  </div>
                  <div className="mb-2 text-sm text-slate-200">
                    {t('Prices')}: <b className="tabular">{preview.preview.impact.pairs}</b> {t('of')} {preview.preview.totalPairs} {t('pairs move')} ·{' '}
                    <b className="tabular">{(preview.preview.impact.revenueShare * 100).toFixed(1)} %</b> {t('of revenue')} · Δ {t('target')}{' '}
                    <b className={`tabular ${preview.preview.impact.deltaEurAnnual < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                      {preview.preview.impact.deltaEurAnnual < 0 ? '−' : '+'}{Math.abs(Math.round(preview.preview.impact.deltaEurAnnual)).toLocaleString('de-AT')} €
                    </b>{' '}
                    ({(preview.preview.impact.deltaPct * 100).toFixed(2)} %) {t('at last year’s volumes')}
                    {preview.preview.unratedAfter !== preview.preview.unratedBefore && (
                      <span className="ml-2 text-amber-300">{t('unrated pairs')} {preview.preview.unratedBefore} → {preview.preview.unratedAfter}</span>
                    )}
                  </div>
                  {preview.preview.impact.top.length > 0 && (
                    <DataTable
                      rows={preview.preview.impact.top}
                      rowKey={(r) => `${r.customerId}|${r.productId}`}
                      columns={[
                        { key: 'c', header: t('Customer'), render: (r) => r.customerName },
                        { key: 'p', header: t('Article'), render: (r) => r.productName },
                        { key: 'b', header: t('Before'), align: 'right', render: (r) => r.before.toFixed(2) },
                        { key: 'a', header: t('After'), align: 'right', render: (r) => <span className="text-slate-100">{r.after.toFixed(2)}</span> },
                        { key: 'd', header: 'Δ', align: 'right', render: (r) => <span className={r.delta < 0 ? 'text-rose-400' : 'text-emerald-400'}>{r.delta > 0 ? '+' : ''}{r.delta.toFixed(2)} ({(r.deltaPct * 100).toFixed(2)} %)</span> },
                        { key: 'k', header: '', render: (r) => (r.clamped ? <span className="text-xs text-amber-300">{t(r.clamped)} {t('by corridor')}</span> : null) },
                      ]}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {error && <div className="mt-3"><ErrorBox>{error}</ErrorBox></div>}
          {errors && (
            <div className="mt-3">
              <WarnBox>{t('Nothing was imported. Fix the rows below and try again.')}</WarnBox>
              <div className="mt-2">
                <DataTable
                  rows={errors}
                  rowKey={(r, ) => `${r.line}|${r.reason}`}
                  maxHeight="18rem"
                  columns={[
                    { key: 'l', header: t('Line'), align: 'right', render: (r) => r.line },
                    { key: 'r', header: t('Reason'), render: (r) => <span className="text-rose-300">{r.reason}</span> },
                  ]}
                />
              </div>
            </div>
          )}
          {result && (
            <div className="mt-3 space-y-2">
              <InfoBox>
                {t('Imported')} <b>{result.inserted}</b> {t('rows into')} {result.dataset}
                {result.derived && (
                  <>
                    {' '}
                    · {t('derived')} <b>{result.derived.customers}</b> {t('customers')}, <b>{result.derived.products}</b> {t('articles')}, <b>{result.derived.historyRows}</b> {t('monthly rows')}
                    {result.derived.months ? ` (${result.derived.months.from} – ${result.derived.months.to})` : ''}
                  </>
                )}
                {result.modelVersionId ? ` ${t('for version')} ${result.modelVersionId.slice(0, 8)}` : ''}.{' '}
                {result.dropped.length > 0 ? t('{n} rows dropped and counted.', { n: result.dropped.length }) : ''}
              </InfoBox>
              {result.dropped.length > 0 && (
                <DataTable
                  rows={result.dropped}
                  rowKey={(r) => `${r.line}|${r.reason}`}
                  maxHeight="14rem"
                  columns={[
                    { key: 'l', header: t('Line'), align: 'right', render: (r) => r.line },
                    { key: 'r', header: t('Dropped because'), render: (r) => <span className="text-amber-300">{r.reason}</span> },
                  ]}
                />
              )}
            </div>
          )}
        </Panel>
      </div>
      </>
      )}
    </div>
  )
}
